import { debounce, isNumber } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import { useMemo, useState, useEffect, MouseEvent, ChangeEvent, useCallback } from 'react';

import { Card, Table, useTheme, TableBody, useMediaQuery, TableContainer } from '@mui/material';

import { useTranslate } from 'src/locales';
import { PageOptions } from 'src/services/api.types';
import { Invoice } from 'src/services/billing/billing.types';
import { useGetOrganizationInvoicesQuery } from 'src/services/billing/billing.service';

import Scrollbar from 'src/components/scrollbar';
import {
  useTable,
  emptyRows,
  TableNoData,
  TableEmptyRows,
  TableHeadCustom,
  TablePaginationCustom,
} from 'src/components/table';

import InvoicesTableRow from './invoices-table-row';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'amount', label: 'Amount' },
  { id: 'reason', label: 'Billing Reason' },
  { id: 'status', label: 'Status' },
  { id: 'created_at', label: 'Created At' },
  { id: 'controls', width: 120 },
];

const DEFAULT_PAGE: PageOptions = {
  page: 1,
  per_page: 5,
};

type Props = {
  organizationId: string;
}

export default function InvoicesTable({
  organizationId
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams();

  const table = useTable();

  const { t } = useTranslate();

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const page = searchParams.get('page');
  const per_page = searchParams.get('per_page');

  const [currentPage, setCurrentPage] = useState<PageOptions>({
    page: page && isNumber(+page) ? +page : DEFAULT_PAGE.page,
    per_page: per_page && isNumber(+per_page) ? +per_page : DEFAULT_PAGE.per_page,
  });

  const [filterQuery, setFilterQuery] = useState<string>('');

  const [totalRows, setTotalRows] = useState<number>(0);

  const { currentData: invoices, isFetching, currentData } = useGetOrganizationInvoicesQuery({
    organization_id: organizationId,
    page_options: {
      page: currentPage.page,
      per_page: currentPage.per_page,
    }
  }, {
    skip: !organizationId
  });

  useEffect(() => {
    if (isFetching) return;

    setTableData(invoices?.results ?? []);
    setTotalRows(invoices?.count ?? 0);
  }, [invoices, isFetching]);

  const denseHeight = table.dense ? 56 : 56 + 20;

  const canReset = !!filterQuery.length;

  const [tableData, setTableData] = useState<Invoice[]>([]);

  const notFound = (!tableData.length && canReset) || !tableData.length;

  const handleFilters = useMemo(
    () => debounce((value: string) => {
      setFilterQuery(value);
    }, 500),
    [setFilterQuery]
  );

  const onChangePage = useCallback(
    (_event: MouseEvent<HTMLButtonElement, MouseEvent>, pageNumber: number) => {
      setCurrentPage({
        ...currentPage,
        page: pageNumber + 1,
      });

      setSearchParams(
        {
          page: (pageNumber + 1).toString(),
          per_page: currentPage.per_page.toString(),
        },
        {
          replace: false,
        }
      );
    },
    [currentPage, setSearchParams]
  );

  const onChangePageRows = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const new_per_page = +(event.target as HTMLInputElement).value;
      setCurrentPage({
        ...DEFAULT_PAGE,
        per_page: new_per_page,
      });
      setSearchParams(
        {
          page: currentPage.page.toString(),
          per_page: new_per_page.toString(),
        },
        {
          replace: false,
        }
      );
    },
    [currentPage.page, setSearchParams]
  );

  return (
    <Card>
        <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'}>
              {
                !isMobile && (
                  <TableHeadCustom
                    order={table.order}
                    headLabel={TABLE_HEAD}
                    rowCount={tableData.length}
                    numSelected={table.selected.length}
                  />
                )
              }

              <TableBody>
                {tableData.map((row) => (
                  <InvoicesTableRow key={row.id} row={row} />
                ))}

                <TableEmptyRows
                  height={denseHeight}
                  emptyRows={emptyRows(table.page, table.rowsPerPage, tableData.length)}
                />

                <TableNoData notFound={notFound} />
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={totalRows}
          page={currentPage.page}
          rowsPerPage={currentPage.per_page}
          onPageChange={onChangePage as any}
          onRowsPerPageChange={onChangePageRows}
          dense={table.dense}
          onChangeDense={table.onChangeDense}
        />
      </Card>
  );
}
